import React, { Ref } from 'react'
import { css } from '@emotion/css'
import { metadataCss } from '../text'
import { TextBox } from '../form-text-box'
import { FormTextProps, FormTextContainer } from '.'

/**
 * Props that aren't otherwise explained are simply passed as attributes
 * to the input element
 */
export interface TextInputProps extends FormTextProps {
  readonly 'aria-labelledby'?: string
  readonly 'data-testid'?: string

  readonly disabled?: boolean

  /**
   * Will be passed as the ref to the actual input element
   */
  readonly inputRef?: Ref<HTMLInputElement>

  readonly min?: number
  readonly max?: number
  readonly pattern?: string

  readonly step?: number
  readonly type?:
    | 'date'
    | 'email'
    | 'number'
    | 'password'
    | 'text'
    | 'url'
    | 'tel'

  /**
   * If supplied and if the input type is number, unitLabel will be displayed
   * as an indicator of the unit for the input field
   */
  readonly unitLabel?: string

  readonly autoFocus?: boolean
}

export function TextInput({
  step,
  type = 'text',
  inputRef,
  max,
  min,
  pattern,
  unitLabel,
  autoFocus,
  onBlur,
  ...props
}: TextInputProps) {
  return (
    <FormTextContainer {...props}>
      {({
        disabled,
        errorMessage,
        id,
        label,
        onChange,
        placeholder,
        renderLabel,
        value,
        tabIndex,
        showRequiredIndicator: _showRequiredIndicator,
        ...props
      }) => (
        <React.Fragment>
          <TextBox
            {...props}
            autoFocus={autoFocus}
            // provide aria-label when we can't ensure a label is associated
            // with the control
            aria-label={renderLabel || !id ? label : undefined}
            disabled={disabled}
            error={!!errorMessage}
            id={id}
            inputRef={inputRef}
            max={max}
            min={min}
            onChange={onChange ? (e) => onChange(e.target.value) : undefined}
            onBlur={onBlur}
            pattern={pattern}
            placeholder={disabled ? undefined : placeholder}
            step={step}
            type={type}
            value={value}
            tabIndex={tabIndex}
          />
          {type === 'number' && unitLabel && (
            <div
              className={css`
                position: absolute;
                right: 28px;
                bottom: 9px;
              `}
            >
              <span className={metadataCss}>{unitLabel}</span>
            </div>
          )}
        </React.Fragment>
      )}
    </FormTextContainer>
  )
}
